import React from 'react';
import { Popover, Button, Typography, Space } from 'antd';
import {
  MAIL_VARIABLES,
  CSV_FIXED_VARIABLES,
  REVERT_NOTIFY_VARIABLES,
  MAIL_VARIABLE_GROUPS,
  toTag,
} from '../../Config/mailVariables';

const { Text } = Typography;

/**
 * MailVariablePopover — システム変数の一覧・挿入ボタン
 * ------------------------------------------------------------------
 * 従来は Popover 内に <li> で変数名を静的に列挙していたが、手入力による誤記
 * （DB フィールド名 lastname / name 等）を防ぐため、各変数をクリックすると
 * 編集中のフィールド（件名 or 本文）のカーソル位置に {$変数$} を挿入する方式に変更。
 * 変数の一覧は Config/mailVariables.js（単一の出典）から生成する。
 *
 * @param {object}   props
 * @param {'survey'|'csv'|'revert'} props.kind
 *   'survey' = サーベイお知らせ配信用 / 'csv' = CSVから配信用 / 'revert' = 回答差戻し通知テンプレート用
 * @param {function} props.onInsert(key) 変数クリック時のコールバック（親がカーソル位置へ挿入）
 */
const MailVariablePopover = ({ kind = 'survey', onInsert }) => {
  const isCsv = kind === 'csv';
  const isRevert = kind === 'revert';

  const renderGroup = (groupKey, vars) => {
    const g = MAIL_VARIABLE_GROUPS[groupKey];
    return (
      <div key={groupKey} style={{ marginBottom: 'var(--space-3)' }}>
        <Text strong style={{ display: 'block', marginBottom: 'var(--space-1)' }}>
          {g.label}
        </Text>
        {g.conditional && (
          <Text
            type="secondary"
            style={{
              display: 'block',
              fontSize: 'var(--text-xs)',
              marginBottom: 'var(--space-1)',
            }}
          >
            {g.note}
          </Text>
        )}
        <Space direction="vertical" size={2} style={{ width: '100%' }}>
          {vars.map((v) => (
            <div
              key={v.key}
              style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}
            >
              <Button
                size="small"
                type="default"
                onClick={() => onInsert && onInsert(v.key)}
                style={{ fontFamily: 'Menlo, Consolas, monospace', minWidth: 190, textAlign: 'left' }}
                title="クリックでカーソル位置に挿入"
              >
                {toTag(v.key)}
              </Button>
              <Text style={{ fontSize: 'var(--text-sm)' }}>
                {v.label}
                {v.example ? `（例：${v.example}）` : ''}
              </Text>
            </div>
          ))}
        </Space>
      </div>
    );
  };

  const surveyContent = (
    <div style={{ maxWidth: 520 }}>
      <Text style={{ display: 'block', marginBottom: 'var(--space-2)' }}>
        システム変数はここに列挙されているものに限って利用できます。
        <br />
        変数をクリックすると、編集中の件名または本文のカーソル位置に {'{$変数名$}'} を挿入します。
      </Text>
      <Text
        type="danger"
        style={{ display: 'block', fontSize: 'var(--text-xs)', marginBottom: 'var(--space-3)' }}
      >
        ※ DB のフィールド名（lastname / name 等）は変数ではありません。姓は familyname、姓名は fullname を使ってください。
      </Text>
      {['user', 'season', 'target360'].map((gk) =>
        renderGroup(
          gk,
          MAIL_VARIABLES.filter((v) => v.group === gk)
        )
      )}
    </div>
  );

  const csvContent = (
    <div style={{ maxWidth: 520 }}>
      <Text style={{ display: 'block', marginBottom: 'var(--space-2)' }}>
        CSVファイルの第１列には「email」を必ず記入する必要があります。
        <br />
        それ以外は CSV ファイルに定義されているフィールド名（英数）をそのまま変数に使うことができます。
        <br />
        {'※変数は {$変数名$} の形で記述ください。'}
      </Text>
      <Text
        type="secondary"
        style={{ display: 'block', fontSize: 'var(--text-xs)', marginBottom: 'var(--space-3)' }}
      >
        ※ CSV 独自の列名は「変数チェック」で未定義として表示されますが、CSVから配信では置換されるため保存できます。
      </Text>
      {renderGroup('csv', CSV_FIXED_VARIABLES)}
    </div>
  );

  const revertContent = (
    <div style={{ maxWidth: 520 }}>
      <Text style={{ display: 'block', marginBottom: 'var(--space-2)' }}>
        回答差戻し通知で置換されるのは、ここに列挙されている変数のみです。
        <br />
        変数をクリックすると、編集中の件名または本文のカーソル位置に {'{$変数名$}'} を挿入します。
      </Text>
      <Text
        type="danger"
        style={{ display: 'block', fontSize: 'var(--text-xs)', marginBottom: 'var(--space-3)' }}
      >
        ※ サーベイお知らせ配信用の変数（company / due_datetime 等）は、差戻し通知では置換されません。
      </Text>
      {renderGroup('revert', REVERT_NOTIFY_VARIABLES)}
    </div>
  );

  const title = isRevert
    ? '回答差戻し通知で使えるシステム変数'
    : isCsv
    ? 'CSVから配信で使えるシステム変数'
    : 'サーベイお知らせ配信で使えるシステム変数';
  const buttonLabel = isRevert
    ? '回答差戻し通知用システム変数'
    : isCsv
    ? 'CSVから配信用システム変数'
    : 'サーベイお知らせ配信用システム変数';

  return (
    <Popover
      trigger="click"
      placement="bottomLeft"
      title={title}
      content={isRevert ? revertContent : isCsv ? csvContent : surveyContent}
      styles={{ body: { maxHeight: '70vh', overflowY: 'auto' } }}
    >
      <Button size="small">{buttonLabel}</Button>
    </Popover>
  );
};

export default MailVariablePopover;
